/**
 * Unified Size System
 * Consistent size definitions for all UI components (ActionButton, Select components, etc.)
 */

export type ComponentSize = 'xs' | 'small' | 'medium' | 'large';

export interface SizeConfig {
  padding: string;
  fontSize: number;
  minHeight: number;
}

// Unified size configurations used across all components
export const SIZE_CONFIGS: Record<ComponentSize, SizeConfig> = {
  xs: {
    padding: '0px 0px',
    fontSize: 8,
    minHeight: 10,
  },
  small: {
    padding: '4px 12px',
    fontSize: 12,
    minHeight: 28,
  },
  medium: {
    padding: '8px 16px',
    fontSize: 13,
    minHeight: 36,
  },
  large: {
    padding: '12px 24px',
    fontSize: 15,
    minHeight: 44,
  },
};

/**
 * Get consistent size styles for any component
 * @param size - The component size
 * @returns Size configuration object
 */
export const getSizeConfig = (size: ComponentSize): SizeConfig => {
  return SIZE_CONFIGS[size];
};

/**
 * Geometry for the large tabbed entity modals (Account, Location).
 *
 * These two dialogs are the same kind of thing — "configure this entity",
 * seven tabs, a footer — and used to declare their own sizes independently:
 * the Account modal was min(960px,95vw)/min(1280px,95vw) while the Location
 * modal was min(1024px,96vw)/min(1200px,95vw). Different at every breakpoint,
 * including a stray 96vw, so opening one after the other visibly shifted.
 *
 * `minHeight` is the other half of holding still. Without a floor the modal is
 * content-sized, so switching from a short tab to a long one resized the frame
 * mid-task. The floor is set near the height of a TYPICAL panel rather than the
 * tallest — the few long panels scroll inside it, and every short tab avoids
 * opening onto a screen of empty space.
 *
 * Anything else that grows into a multi-tab entity editor should use these
 * rather than inventing a third set.
 */
export const ENTITY_MODAL = {
  width: {
    tablet: 'min(1024px, 95vw)',
    desktop: 'min(1240px, 95vw)',
  },
  /** Applied on tablet/desktop only; mobile is already full-bleed. */
  minHeight: '620px',
} as const;
